/**
 * The plants context's shape and the context itself, apart from
 * PlantsProvider so hooks and components can import the type without the
 * provider (and so Fast Refresh keeps the provider file components-only).
 *
 * @module PlantsContextDefinition
 */
import { createContext } from 'react';
import { Plant } from '../types/plant';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import type { MergeResult } from '@/shared/api/plantMerge';

/** The gardener's plants, and the ways to add, change, retire and restore them. */
export interface PlantsContextType {
  /** The garden's plants; undefined until the first read comes back. */
  plants: Plant[] | undefined;
  /** True only while there is nothing to show yet (not during a background refetch). */
  isLoading: boolean;
  /** Signed in, and the server says the garden has no postcode Grow can plan from: the plants load without their climate. */
  missingZipCode: boolean;
  /** The read's failure, or null. */
  error: Error | null;
  /** Read the whole garden again. */
  refetchPlants: () => void;
  /** Read one plant again and splice it into the list. */
  updateSinglePlant: (plantObjId: string) => Promise<void>;
  /** Adds a plant; the new plant's id comes back on success. */
  addPlant: (payload: PlantWritePayload) => Promise<{ success: boolean, plant_obj_id?: string | number, error?: string }>;
  /** Changes an existing plant's variety, bed or status; what is left out keeps its value (a patch). */
  editPlant: (plantObjId: number, payload: PlantWritePayload) => Promise<{ success: boolean, error?: string }>;
  /** Removes a plant from the garden by archiving it. Nothing is deleted. */
  archivePlant: (plantObjId: number) => Promise<{ success: boolean, error?: string }>;
  /**
   * Puts an archived plant back in the garden.
   *
   * Here rather than at each call site because leaving and rejoining the
   * garden have to bring the same things back in step, and when only one of
   * them did, a restored plant kept last week's advice.
   */
  restorePlant: (plantObjId: number) => Promise<{ success: boolean, error?: string }>;
  /**
   * Merges a plant into another (D66) and reads the garden again. Throws on a refusal or a
   * failure, for the merge sheet to word.
   */
  mergePlantInto: (goneId: number, keepId: number, varietyId: number) => Promise<MergeResult>;
}

/** The context, undefined outside PlantsProvider so usePlantsContext can say so. */
export const PlantsContext = createContext<PlantsContextType | undefined>(undefined);